import { useState, type FormEvent, type JSX } from 'react';

import { useAuthStore } from '../../state/auth.js';

/**
 * Dev-only auto-login affordance. Renders a "Dev access" button below the
 * sign-in form when the SPA is running under `vite dev` (i.e.
 * `import.meta.env.DEV === true`). Production builds tree-shake the branch
 * out via Vite's static `MODE` replacement, so the button never reaches a
 * deployed bundle.
 *
 * The shortcut posts the workspace's seeded dev admin (`admin` / `admin`).
 * If you've rotated the admin password the click will surface a normal
 * login error — fall back to the regular form.
 */
const DEV_ADMIN_USERNAME = 'admin';
// 10 chars to clear the user-store's 8-char password floor while keeping
// the dev-loop symmetric with the username. Synced with the seed value
// passed via `--admin-password` when the dev backend boots against the
// debug-workspace fixture.
const DEV_ADMIN_PASSWORD = 'adminadmin';

export function LoginPage(): JSX.Element {
  const status = useAuthStore((s) => s.status);
  const error = useAuthStore((s) => s.error);
  const login = useAuthStore((s) => s.login);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const busy = status === 'loading';

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (busy) return;
    await login(username, password);
  }

  async function onDevAccess(): Promise<void> {
    if (busy) return;
    await login(DEV_ADMIN_USERNAME, DEV_ADMIN_PASSWORD);
  }

  return (
    <div className="bg-cream-50 text-fg flex min-h-screen items-center justify-center">
      <form
        onSubmit={(e) => {
          void onSubmit(e);
        }}
        className="border-taupe-300 bg-cream-50 w-full max-w-md space-y-6 border p-10"
      >
        <div className="flex flex-col gap-3">
          <div className="bg-audi-red h-[3px] w-10" aria-hidden="true" />
          <span className="text-audi-red text-[11px] font-semibold uppercase tracking-[0.22em]">
            Sign in
          </span>
          <h1 className="text-fg text-3xl font-extrabold leading-[1.05] tracking-[-0.02em]">
            TESSA Lean
          </h1>
          <p className="text-taupe-700 text-sm">
            Engineering knowledge-base assistant
          </p>
        </div>

        <label className="block text-sm">
          <span className="text-taupe-700 mb-1 block text-[11px] font-semibold uppercase tracking-[0.12em]">
            Username
          </span>
          <input
            type="text"
            autoComplete="username"
            autoFocus
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            disabled={busy}
            className="bg-cream-50 border-taupe-500 text-fg focus:border-audi-red focus:ring-audi-red w-full border px-3 py-2 focus:outline-none focus:ring-1"
            required
          />
        </label>
        <label className="block text-sm">
          <span className="text-taupe-700 mb-1 block text-[11px] font-semibold uppercase tracking-[0.12em]">
            Password
          </span>
          <input
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={busy}
            className="bg-cream-50 border-taupe-500 text-fg focus:border-audi-red focus:ring-audi-red w-full border px-3 py-2 focus:outline-none focus:ring-1"
            required
          />
        </label>
        {error !== null ? (
          <div
            role="alert"
            className="border-audi-red bg-accent-red-bg text-audi-red border px-3 py-2 text-sm"
          >
            {error}
          </div>
        ) : null}
        <button
          type="submit"
          disabled={busy || username.length === 0 || password.length === 0}
          className="bg-taupe-900 hover:bg-audi-red focus-visible:ring-taupe-900 w-full px-4 py-3 text-sm font-semibold uppercase tracking-[0.12em] text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
        {import.meta.env.DEV ? (
          <button
            type="button"
            data-testid="dev-access-button"
            onClick={() => {
              void onDevAccess();
            }}
            disabled={busy}
            title={`Auto-login as ${DEV_ADMIN_USERNAME}. Visible only in vite dev — stripped from production builds.`}
            className="border-taupe-500 text-taupe-700 hover:border-audi-red hover:text-audi-red w-full border-2 border-dashed px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] transition-colors disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? 'Signing in…' : 'Dev access (admin)'}
          </button>
        ) : null}
      </form>
    </div>
  );
}
